/*eslint-disable*/
import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Icon,
  Stack,
  Text,
  Tooltip,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import AuthorizationWrapper from "components/AuthorizationWrapper/AuthorizationWrapper";
import IconBox from "components/Icons/IconBox";
import {
  renderThumbDark,
  renderThumbLight,
  renderTrack,
  renderTrackRTL,
  renderView,
  renderViewRTL,
} from "components/Scrollbar/Scrollbar";
import { HSeparator } from "components/Separator/Separator";
import React, { useEffect, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { NavLink, useLocation } from "react-router-dom";
import { useUserState } from "context/UserContext";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useTranslation } from "i18n";

// Walk routes (incl. nested views) to find which collapse group owns `pathname`.
const findActiveCollapse = (routes, pathname) => {
  for (const r of routes || []) {
    if (r.collapse && Array.isArray(r.views)) {
      const hit = r.views.some(
        (v) => v && !v.redirect && v.layout && v.path && `${v.layout}${v.path}` === pathname
      );
      if (hit) return r.collapse;
    }
    if (r.category && Array.isArray(r.views)) {
      const nested = findActiveCollapse(r.views, pathname);
      if (nested) return nested;
    }
  }
  return "";
};

const countVisibleViews = (views) =>
  Array.isArray(views) ? views.filter((v) => v && !v.redirect && v.path && !v.category).length : 0;

// FUNCTIONS

function Sidebar(props) {
  // to check for active links and opened collapses
  let location = useLocation();
  // this is for the rest of the collapses
  const [state, setState] = React.useState({});
  const mainPanel = React.useRef();
  let variantChange = "0.2s linear";
  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => {
    return location.pathname === routeName ? "active" : "";
  };
  const { userInfo } = useUserState();
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  // this function creates the links and collapses that appear in the sidebar (left menu)
  const { sidebarVariant, routes } = props;
  // Multiple groups can be open at once — clicking a group header only toggles
  // that group. Navigation (clicking a record/leaf) is what closes groups.
  const [openGroups, setOpenGroups] = useState(() => {
    const active = findActiveCollapse(routes, location.pathname);
    return new Set(active ? [active] : []);
  });

  useEffect(() => {
    const active = findActiveCollapse(routes, location.pathname);
    if (active) {
      // navigated into a group's record — open only that group, close others
      setOpenGroups((prev) => {
        if (prev.size === 1 && prev.has(active)) return prev;
        return new Set([active]);
      });
    } else {
      // navigated to a record outside any group — close everything
      setOpenGroups((prev) => (prev.size === 0 ? prev : new Set()));
    }
  }, [location.pathname, routes]);

  const toggleGroup = (collapse) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(collapse)) next.delete(collapse);
      else next.add(collapse);
      return next;
    });
  };

  // ── All color tokens hoisted to component body — no hooks inside createLinks ──
  // Active: indigo pill gradient — premium elegant highlight
  const activeBg = useColorModeValue(
    "linear-gradient(to right, rgba(224,231,255,0.95), rgba(224,231,255,0.35))",
    "linear-gradient(to right, rgba(99,102,241,0.24), rgba(99,102,241,0.06))"
  );
  const activeColor = useColorModeValue("brand.700", "brand.300");
  const inactiveColor = useColorModeValue("gray.600", "gray.400");
  const sidebarActiveShadow = useColorModeValue(
    "0 4px 14px rgba(99,102,241,0.26), 0 2px 4px rgba(99,102,241,0.14)",
    "0 4px 14px rgba(99,102,241,0.34)"
  );
  const hoverBg = useColorModeValue("rgba(224,231,255,0.55)", "whiteAlpha.100");
  const hoverBorderColor = useColorModeValue("brand.100", "whiteAlpha.100");
  const activeBorderColor = useColorModeValue("brand.200", "brand.700");
  const activeHoverBorderColor = useColorModeValue("brand.300", "brand.600");
  const iconActiveBg = useColorModeValue("brand.500", "brand.400");
  const iconActiveShadow = useColorModeValue(
    "0 4px 12px rgba(99,102,241,0.48), 0 2px 4px rgba(99,102,241,0.28)",
    "0 4px 12px rgba(99,102,241,0.58)"
  );
  const iconInactiveBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const iconInactiveColor = useColorModeValue("brand.600", "brand.300");
  const logoTextColor = useColorModeValue("gray.800", "white");
  const separatorGradient = useColorModeValue(
    "linear(to-r, transparent, brand.200, transparent)",
    "linear(to-r, transparent, brand.800, transparent)"
  );

  const createLinks = (routes) => {
    return routes.map((prop, key) => {
      if (prop.redirect) {
        return null;
      }
      if (prop.collapse) {
        const isOpen = openGroups.has(prop.collapse);
        const viewCount = countVisibleViews(prop.views);
        const groupLabel =
          document.documentElement.dir === "rtl"
            ? prop.rtlName
            : prop.nameKey
            ? t(prop.nameKey)
            : prop.name;
        return (
          <React.Fragment key={key}>
            <Tooltip
              label={groupLabel}
              placement="right"
              hasArrow
              openDelay={400}
              gutter={12}
            >
            <Button
              boxSize="initial"
              justifyContent="flex-start"
              alignItems="center"
              bg="transparent"
              mb={{ xl: "6px" }}
              mx={{ xl: "auto" }}
              py="12px"
              ps={{ sm: "10px", xl: "16px" }}
              borderRadius="12px"
              w="100%"
              border="1px solid transparent"
              transition="all 0.18s cubic-bezier(0.4, 0, 0.2, 1)"
              pe={{ sm: "8px", xl: "10px" }}
              _hover={{ bg: hoverBg, borderColor: hoverBorderColor }}
              _active={{ transform: "scale(0.98)", borderColor: "transparent" }}
              _focus={{ boxShadow: "none" }}
              aria-expanded={isOpen}
              onClick={() => toggleGroup(prop.collapse)}
            >
              <Flex
                alignItems={"center"}
                width={"100%"}
                justifyContent={"space-between"}
                gap="6px"
              >
                <Flex alignItems="center" minW={0} flex="1">
                  {typeof prop.icon === "string" ? (
                    <Icon>{prop.icon}</Icon>
                  ) : (
                    <IconBox
                      bg={iconInactiveBg}
                      color={iconInactiveColor}
                      h="30px"
                      w="30px"
                      me="12px"
                      borderRadius="8px"
                      transition="all 0.18s cubic-bezier(0.4, 0, 0.2, 1)"
                    >
                      {prop.icon}
                    </IconBox>
                  )}
                  <Text
                    color={inactiveColor}
                    my="auto"
                    fontSize="14px"
                    noOfLines={1}
                    minW={0}
                  >
                    {document.documentElement.dir === "rtl"
                      ? prop.rtlName
                      : prop.nameKey
                      ? t(prop.nameKey)
                      : `${prop.name}`}
                  </Text>
                  {viewCount > 0 && (
                    <Badge
                      ml="8px"
                      px="6px"
                      py="2px"
                      borderRadius="full"
                      fontSize="10px"
                      fontWeight="semibold"
                      flexShrink={0}
                      bg={iconInactiveBg}
                      color={iconInactiveColor}
                      textTransform="none"
                    >
                      {viewCount}
                    </Badge>
                  )}
                </Flex>
                <Box color={inactiveColor} opacity={0.7} flexShrink={0}>
                  {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                </Box>
              </Flex>
            </Button>
            </Tooltip>
            <Collapse
              in={isOpen}
              animateOpacity
              unmountOnExit
              transition={{
                enter: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
                exit: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
              }}
            >
              <Box ml={3} mr={3} mb={2}>
                {createLinks(prop.views)}
              </Box>
            </Collapse>
          </React.Fragment>
        );
      }
      if (prop.category) {
        var st = {};
        st[prop["state"]] = !state[prop.state];
        return (
          <React.Fragment key={key}>
            <Text
              color={activeColor}
              fontWeight="bold"
              mb={{
                xl: "6px",
              }}
              mx="auto"
              ps={{
                sm: "10px",
                xl: "16px",
              }}
              py="12px"
            >
              {document.documentElement.dir === "rtl"
                ? prop.rtlName
                : prop.nameKey
                ? t(prop.nameKey)
                : `${prop.name}`}
            </Text>
            {createLinks(prop.views)}
          </React.Fragment>
        );
      }
      return (
        <AuthorizationWrapper
          allowedRoles={prop.role}
          key={key}
          groupPermission={userInfo?.permissions}
          groupName={prop?.groupName}
        >
          <NavLink
            to={prop.layout + prop.path}
            key={key}
            {...(prop.layout === "/legal"
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            <Tooltip
              label={
                document.documentElement.dir === "rtl"
                  ? prop.rtlName
                  : prop.nameKey
                  ? t(prop.nameKey)
                  : prop.name
              }
              placement="right"
              hasArrow
              openDelay={400}
              gutter={12}
            >
            {activeRoute(prop.layout + prop.path) === "active" ? (
              <Button
                boxSize="initial"
                justifyContent="flex-start"
                alignItems="center"
                boxShadow={sidebarActiveShadow}
                // Pill gradient bg — no border-left hack, cleaner visual
                background={activeBg}
                transition="all 0.18s cubic-bezier(0.4, 0, 0.2, 1)"
                mb={{ xl: "6px" }}
                mx={{ xl: "auto" }}
                ps={{ sm: "10px", xl: "16px" }}
                py="12px"
                borderRadius="12px"
                w="100%"
                border="1px solid"
                borderColor={activeBorderColor}
                _hover={{ background: activeBg, borderColor: activeHoverBorderColor }}
                _active={{
                  transform: "scale(0.98)",
                  borderColor: "transparent",
                }}
                _focus={{
                  boxShadow: sidebarActiveShadow,
                }}
              >
                <Flex>
                  {typeof prop.icon === "string" ? (
                    <Icon>{prop.icon}</Icon>
                  ) : (
                    <IconBox
                      // Active icon: colored bg with brand glow
                      bg={iconActiveBg}
                      color="white"
                      h="30px"
                      w="30px"
                      me="12px"
                      borderRadius="8px"
                      boxShadow={iconActiveShadow}
                      transition="all 0.18s cubic-bezier(0.4, 0, 0.2, 1)"
                    >
                      {prop.icon}
                    </IconBox>
                  )}
                  <Text color={activeColor} my="auto" fontSize="14px" fontWeight="semibold">
                    {document.documentElement.dir === "rtl"
                      ? prop.rtlName
                      : prop.nameKey
                      ? t(prop.nameKey)
                      : `${prop.name}`}
                  </Text>
                </Flex>
              </Button>
            ) : (
              <Button
                boxSize="initial"
                justifyContent="flex-start"
                alignItems="center"
                bg="transparent"
                mb={{ xl: "6px" }}
                mx={{ xl: "auto" }}
                py="12px"
                ps={{ sm: "10px", xl: "16px" }}
                borderRadius="12px"
                w="100%"
                border="1px solid transparent"
                transition="all 0.18s cubic-bezier(0.4, 0, 0.2, 1)"
                _hover={{
                  bg: hoverBg,
                  borderColor: hoverBorderColor,
                }}
                _active={{
                  transform: "scale(0.98)",
                  borderColor: "transparent",
                }}
                _focus={{ boxShadow: "none" }}
              >
                <Flex>
                  {typeof prop.icon === "string" ? (
                    <Icon>{prop.icon}</Icon>
                  ) : (
                    <IconBox
                      // Inactive: soft brand-tinted bg, icon brand.500
                      bg={iconInactiveBg}
                      color={iconInactiveColor}
                      h="30px"
                      w="30px"
                      me="12px"
                      borderRadius="8px"
                      transition="all 0.18s cubic-bezier(0.4, 0, 0.2, 1)"
                    >
                      {prop.icon}
                    </IconBox>
                  )}
                  <Text color={inactiveColor} my="auto" fontSize="14px">
                    {document.documentElement.dir === "rtl"
                      ? prop.rtlName
                      : prop.nameKey
                      ? t(prop.nameKey)
                      : `${prop.name}`}
                  </Text>
                </Flex>
              </Button>
            )}
            </Tooltip>
          </NavLink>
        </AuthorizationWrapper>
      );
    });
  };
  const { logo } = props;

  var links = <>{createLinks(routes)}</>;
  //  BRAND
  //  Chakra Color Mode
  const sidebarBg = useColorModeValue(
    "#FFFFFF",
    "linear-gradient(180deg, #1e2a44 0%, #111c44 100%)"
  );

  let sidebarRadius = "20px";
  let sidebarMargins = "0px";
  const logoBg = useColorModeValue("brand.500", "brand.400");
  const logoSubColor = useColorModeValue("gray.500", "gray.400");

  var brand = (
    <Box pt="22px" mb="8px">
      {/* Logo mark + wordmark */}
      <Flex direction="column" alignItems="center" gap="2px">
        {/* Icon badge */}
        <Flex
          w="40px"
          h="40px"
          bg={logoBg}
          borderRadius="10px"
          alignItems="center"
          justifyContent="center"
          mb="10px"
          boxShadow="0 6px 18px rgba(99,102,241,0.45), 0 2px 6px rgba(99,102,241,0.25)"
        >
          <Text color="white" fontSize="lg" fontWeight="bold" lineHeight="1">S</Text>
        </Flex>
        <Text
          fontSize="sm"
          fontWeight="bold"
          letterSpacing="wider"
          textTransform="uppercase"
          color={logoTextColor}
        >
          Social Panel
        </Text>
        <Text
          fontSize="xs"
          color={logoSubColor}
          letterSpacing="widest"
          textTransform="uppercase"
        >
          Dashboard
        </Text>
      </Flex>
      {/* Subtle separator with brand tint — gradient fades at edges */}
      <Box
        mt="18px"
        mb="8px"
        h="1px"
        bgGradient={separatorGradient}
      />
    </Box>
  );

  // SIDEBAR
  return (
    <Box ref={mainPanel}>
      <Box display={{ sm: "none", xl: "block" }} position="fixed">
        <Box
          backdropFilter="blur(21px)"
          boxShadow="0 12px 32px rgba(15, 23, 42, 0.12), 0 4px 12px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)"
          bg={sidebarBg}
          transition={variantChange}
          w="290px"
          maxW="290px"
          ms={{
            sm: "16px",
          }}
          my={{
            sm: "16px",
          }}
          h="calc(100vh - 32px)"
          ps="20px"
          pe="20px"
          m={sidebarMargins}
          borderWidth="1px"
          borderColor="gray.100"
          borderRadius={sidebarRadius}
        >
          <Scrollbars
            autoHide
            renderTrackVertical={
              document.documentElement.dir === "rtl"
                ? renderTrackRTL
                : renderTrack
            }
            renderThumbVertical={useColorModeValue(
              renderThumbLight,
              renderThumbDark
            )}
            renderView={
              document.documentElement.dir === "rtl"
                ? renderViewRTL
                : renderView
            }
          >
            <Box>{brand}</Box>
            <Stack direction="column" mb="40px">
              <Box>{links}</Box>
            </Stack>
          </Scrollbars>
        </Box>
      </Box>
    </Box>
  );
}

export { SidebarResponsive } from './SidebarResponsive';

export default Sidebar;
